import merge from 'lodash/merge';
import { useMemo } from 'react';
// @mui
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MuiThemeProvider, ThemeOptions } from '@mui/material/styles';
// components
import { useSettingsContext } from 'src/components/settings';
// system
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';
import { customShadows } from './custom-shadows';
import { componentsOverrides } from './overrides';
// options
import { presets } from './options/presets';
import { darkMode } from './options/dark-mode';
import { contrast } from './options/contrast';
import RTL, { direction } from './options/right-to-left';
// eslint-disable-next-line import/no-extraneous-dependencies
import '@fontsource/amiri-quran';
// eslint-disable-next-line import/no-extraneous-dependencies
import '@fontsource-variable/readex-pro';
// eslint-disable-next-line import/no-extraneous-dependencies
import '@fontsource-variable/rubik';

type Props = {
  children: React.ReactNode;
};

export default function ThemeProvider({ children }: Props) {
  const settings = useSettingsContext();

  // Memoize settings-dependent options
  const themeOptions = useMemo(() => {
    // Base options
    const baseOption = {
      palette: palette(settings.themeMode),
      shadows: shadows(settings.themeMode),
      customShadows: customShadows(settings.themeMode),
      typography,
      shape: { borderRadius: 8 },
    };

    // Conditional options
    const darkModeOption = settings.themeMode === 'dark' ? darkMode(settings.themeMode) : {};
    const presetsOption = presets(settings.themeColorPresets);
    const contrastOption = contrast(settings.themeContrast === 'bold', settings.themeMode);
    const directionOption = direction(settings.themeDirection);

    // Custom sidebar background color
    const navColorOption =
      settings.themeNavColor === 'dynamic'
        ? {
          components: {
            MuiDrawer: {
              styleOverrides: {
                paper: {
                  backgroundColor: settings.themeMode === 'dark' ? '#252E37' : '#ffffff',
                  color: settings.themeMode === 'dark' ? '#ffffff' : '#000000',
                },
              },
            },
          },
        }
        : {};

    // Merge all options
    return merge(
      baseOption,
      directionOption,
      darkModeOption,
      presetsOption,
      contrastOption.theme,
      navColorOption
    );
  }, [
    settings.themeMode,
    settings.themeColorPresets,
    settings.themeContrast,
    settings.themeDirection,
    settings.themeNavColor,
  ]);

  // Create theme
  const theme = useMemo(() => {
    const baseTheme = createTheme(themeOptions as ThemeOptions);
    baseTheme.components = merge(componentsOverrides(baseTheme), contrast(settings.themeContrast === 'bold', settings.themeMode).components);
    return baseTheme;
  }, [themeOptions, settings.themeContrast, settings.themeMode]);

  return (
    <MuiThemeProvider theme={theme}>
      <RTL themeDirection={settings.themeDirection}>
        <CssBaseline />
        {children}
      </RTL>
    </MuiThemeProvider>
  );
}
